import * as React from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { cn } from '../../utils';
import { Button } from '../Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from '../Command';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { ScrollArea } from '../ScrollArea';
import { CountrySelectOption } from './CountrySelectOption';
import { FlagComponent } from './FlagComponent';
import { CountrySelectProps } from './types';

export const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
  searchPlaceholder,
  notFoundText,
}: CountrySelectProps) => {
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Popover
      open={isOpen}
      modal
      onOpenChange={open => {
        setIsOpen(open);
        if (open) setSearchValue('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="border-border-soft disabled:border-border-soft flex h-10 gap-1 rounded-s-lg rounded-e-none border-[1.5px] border-r-0 px-3 focus:z-10 focus-visible:ring-0 focus-visible:ring-offset-0"
          disabled={disabled}
          tabIndex={-1}
        >
          <FlagComponent
            country={selectedCountry}
            countryName={selectedCountry}
          />
          <ChevronsUpDown
            className={cn(
              '-mr-2 size-4 opacity-50',
              disabled ? 'hidden' : 'opacity-100'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[300px] p-0">
        <Command>
          <CommandInput
            value={searchValue}
            onValueChange={value => {
              setSearchValue(value);
              setTimeout(() => {
                if (scrollAreaRef.current) {
                  const viewportElement = scrollAreaRef.current.querySelector(
                    '[data-radix-scroll-area-viewport]'
                  );
                  if (viewportElement) viewportElement.scrollTop = 0;
                }
              }, 0);
            }}
            placeholder={searchPlaceholder}
          />
          <CommandList>
            <ScrollArea ref={scrollAreaRef} className="h-72">
              <CommandEmpty>{notFoundText}</CommandEmpty>
              <CommandGroup>
                {countryList.map(
                  ({ value, label }) =>
                    value && (
                      <CountrySelectOption
                        key={value}
                        country={value}
                        countryName={label}
                        selectedCountry={selectedCountry}
                        onChange={onChange}
                        onSelectComplete={() => setIsOpen(false)}
                      />
                    )
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
